import { inject, Injectable, Injector } from '@angular/core';
import { OrdersService } from '../services/orders.service';
import { Observable } from 'rxjs';
import {
  IArchiveOrder,
  IArchiveOrderResponse,
  ICartsApprovalResponse,
  IOrderById,
  IOrderData,
  IOrderSearchFilter,
  IOrdersResponse,
  IQuoteRequestAcceptRejectResponse,
} from '../models/order.models';
import {
  IOrdersHistoryPage,
  IOrdersWithSapDetails,
  selectCpqOrdersFulfilledPagination,
  selectCpqOrdersUnderReviewPagination,
  selectFulfilledCpqOrdersHistoryAtPage,
  selectLoadedSapOrders,
  selectOrdersHistoryAtPage,
  selectOrdersPendingCarts,
  selectSapOrdersAtPage,
  selectSapOrdersPagination,
  selectUnderReviewCpqOrdersHistoryAtPage,
} from '../reducers/orders.reducer';
import { Store } from '@ngrx/store';
import * as fromState from '../reducers';
import * as OrdersActions from '../actions/orders.actions';
import { selectHybrisOrderById, selectHybrisOrders } from '../reducers/hybris-orders.reducer';
import { AppUtils } from '../utils/app.utils';
import { IPaginationBasic, IPaginationCurrent } from '../models/settings.model';
import { RfqService } from '../services';
import { PurchaseActivityActions } from '../actions';
import { HttpResponse } from '@angular/common/http';


@Injectable({providedIn: 'root'})
export class OrdersFacade {
  private readonly injector: Injector = inject(Injector);
  private readonly store: Store<fromState.State> = inject(Store<fromState.State>);

  private pOrdersService: OrdersService | undefined;
  private pRfqService: RfqService | undefined;

  private getOrdersService(): OrdersService {
    if (!this.pOrdersService) {
      this.pOrdersService = this.injector.get(OrdersService);
    }
    return this.pOrdersService;
  }

  public getRfqService(): RfqService {
    if (!this.pRfqService) {
      this.pRfqService = this.injector.get(RfqService);
    }
    return this.pRfqService;
  }

  getCarts(): Observable<any> {
    return this.getOrdersService().getCarts();
  }

  loadCarts() {
    this.store.dispatch(OrdersActions.OrdersWithCartsStartDataAction());
  }

  getCartsApproval(): Observable<ICartsApprovalResponse> {
    return this.getOrdersService().getCartsApproval();
  }

  loadCartsApproval() {
    // TODO: remove first dispatch after harmonization
    this.store.dispatch(OrdersActions.OrdersWithCartApprovalStartDataAction());
    this.store.dispatch(PurchaseActivityActions.loadOrdersInPendingOrders());
  }

  //calling selector for pending carts from application store
  selectPendingCarts(): Observable<any> {
    return this.store.select(selectOrdersPendingCarts);
  }

  getCartForApprovalData(id: string): Observable<any> {
    return this.getOrdersService().getCartForApprovalData(id);
  }

  postDeclinePendingOrder(data: any): Observable<any> {
    return this.getOrdersService().postDeclinePendingOrder(data);
  }

  deleteCart(cartId: string): Observable<any> {
    return this.getOrdersService().deleteCart(cartId);
  }

  deleteItemFromOrder(cartId: string, itemId: string): Observable<any> {
    return this.getOrdersService().deleteItemFromOrder(cartId, itemId);
  }

  postCancelOrder(orderId: string, data: any): Observable<any> {
    return this.getOrdersService().postCancelOrder(orderId, data);
  }

  getOrders(
    offset: number | undefined = undefined,
    limit: number | undefined = undefined,
    orderFilter: Partial<IOrderSearchFilter> = {},
  ): Observable<IOrdersResponse> {
    return this.getOrdersService().getOrders(offset, limit, orderFilter);
  }

  loadOrdersHistory(
    offset: number | null = null,
    limit: number | null = null,
    recallOnClearData: boolean = false,
  ): void {
    if (!offset || !limit) {
      // user must set both if they wish to override the default store-specific settings
      const paginationSettings: IPaginationBasic = AppUtils.getCurrentStore().paginationSettingsPerFeature.orders;
      offset = 0;
      limit = paginationSettings.limitPerPage;
    }
    if (recallOnClearData) {
      this.store.dispatch(OrdersActions.OrdersHistoryClearDataAction());
    }
    // TODO: remove dispatch after harmonization
    this.store.dispatch(OrdersActions.OrdersHistoryStartDataAction(offset, limit));
  }

  selectOrdersHistoryAtPage(pageNumber: number): Observable<IOrdersHistoryPage> {
    return this.store.select(selectOrdersHistoryAtPage(pageNumber));
  }

  getArchiveOrders(): Observable<IArchiveOrderResponse> {
    return this.getOrdersService().getArchiveOrders();
  }

  loadPreviousQuotesAndRequests() {
    this.store.dispatch(PurchaseActivityActions.loadQuotesInPreviousQuotes());
  }

  selectHybrisOrders(): Observable<IArchiveOrder[] | null> {
    return this.store.select(selectHybrisOrders);
  }

  getOrderById(orderId: string, guestUserId: string = ''): Observable<IOrderById> {
    if (guestUserId) {
      return this.getOrdersService().getGuestOrderById(guestUserId, orderId);
    }

    return this.getOrdersService().getOrderById(orderId);
  }

  getHybrisOrderById(id: string): Observable<IArchiveOrderResponse> {
    return this.getOrdersService().getHybrisOrderById(id);
  }

  selectHybrisOrderById(id: string): Observable<IArchiveOrder | undefined> {
    return this.store.select(selectHybrisOrderById(id));
  }

  getOrderDetailPdfFile(orderReference: string): Observable<any> {
    return this.getOrdersService().getOrderDetailPdfFile(orderReference);
  }

  getQuoteRequestDocumentFile(fileDownloadLink: string): Observable<HttpResponse<Blob>> {
    return this.getOrdersService().getQuoteRequestDocumentFile(fileDownloadLink);
  }

  postOrdersHistoryExcelFile(activeOrdersReferences: string[]): Observable<HttpResponse<Blob>> {
    return this.getOrdersService().postOrdersHistoryExcelFile(activeOrdersReferences);
  }

  postQuoteHistoryExcelFile(activeQuoteReferences: string[]): Observable<HttpResponse<Blob>> {
    return this.getOrdersService().getQuoteHistoryExcelFile(activeQuoteReferences);
  }

  downloadPoDocument(fileLink: string): Observable<HttpResponse<Blob>> {
    return this.getOrdersService().downloadPoDocument(fileLink);
  }

  postHybrisOrdersHistoryExcelFile(activeOrdersReferences: string[]): Observable<any> {
    return this.getOrdersService().postHybrisOrdersHistoryExcelFile(activeOrdersReferences);
  }

  selectLoadedOrdersWithSapDetails(): Observable<IOrdersWithSapDetails> {
    return this.store.select(selectLoadedSapOrders);
  }

  selectOrdersWithSapDetailsAtPage(pageNumber: number): Observable<IOrdersWithSapDetails> {
    return this.store.select(selectSapOrdersAtPage(pageNumber));
  }

  selectOrdersWithSapDetailsPagination(): Observable<IPaginationCurrent> {
    return this.store.select(selectSapOrdersPagination);
  }

  recallAndClearOrdersWithDetailsDataList(
    offset: number | null = null,
    limit: number | null  = null,
  ) {
    if (!offset || !limit) {
      // user must set both if they wish to override the default store-specific settings
      const paginationSettings: IPaginationBasic = AppUtils.getCurrentStore().paginationSettingsPerFeature.sparePartOrders;
      offset = 0;
      limit = paginationSettings.limitPerPage;
    }
    this.store.dispatch(OrdersActions.OrdersWithSapDetailsClearData());
    this.store.dispatch(OrdersActions.OrdersWithSapDetailsStartDataAction(offset, limit));
  }

  loadOrdersWithSapDetails(
    offset: number | null = null,
    limit: number | null  = null,
  ): void {
    if (!offset || !limit) {
      // user must set both if they wish to override the default store-specific settings
      const paginationSettings: IPaginationBasic = AppUtils.getCurrentStore().paginationSettingsPerFeature.serviceOrders;
      offset = 0;
      limit = paginationSettings.limitPerPage;
    }
    this.store.dispatch(OrdersActions.OrdersWithSapDetailsStartDataAction(offset, limit));
  }

  loadCpqOrdersUnderReviewData(
    offset: number | null = null,
    limit: number | null  = null,
  ): void {
    if (!offset || !limit) {
      // user must set both if they wish to override the default store-specific settings
      const paginationSettings: IPaginationBasic = AppUtils.getCurrentStore().paginationSettingsPerFeature.serviceOrders;
      offset = 0;
      limit = paginationSettings.limitPerPage;
    }
    this.store.dispatch(OrdersActions.CpqOrdersUnderReviewStartDataAction(offset, limit));
  }

  loadCpqOrdersFulfilledData(
    offset: number | null = null,
    limit: number | null  = null,
  ): void {
    if (!offset || !limit) {
      // user must set both if they wish to override the default store-specific settings
      const paginationSettings: IPaginationBasic = AppUtils.getCurrentStore().paginationSettingsPerFeature.serviceOrders;
      offset = 0;
      limit = paginationSettings.limitPerPage;
    }
    this.store.dispatch(OrdersActions.CpqOrdersFulfilledStartDataAction(offset, limit));
  }

  selectCpqUnderReviewOrdersAtPage(pageNumber: number): Observable<IOrderData[]> {
    return this.store.select(selectUnderReviewCpqOrdersHistoryAtPage(pageNumber));
  }

  selectCpqFulFilledOrdersAtPage(pageNumber: number): Observable<IOrderData[]> {
    return this.store.select(selectFulfilledCpqOrdersHistoryAtPage(pageNumber));
  }

  selectCpqFulFilledOrdersPagination(): Observable<IPaginationCurrent> {
    return this.store.select(selectCpqOrdersFulfilledPagination);
  }

  selectCpqUnderReviewOrdersPagination(): Observable<IPaginationCurrent> {
    return this.store.select(selectCpqOrdersUnderReviewPagination);
  }

  /**
  * Changes the status of a quote request version.
  * 
  * @param quoteRequestId {string} - ID of the quote request
  * @param status {string} - New status to set
  * @param reason {string | undefined} - Reason for status change (optional)
  * @returns Observable<IQuoteRequestCancelRejectResponse>
  */
  acceptOrRejectRequestQuoteVersion(quoteRequestId: string, status: string, reason: string | undefined): Observable<IQuoteRequestAcceptRejectResponse> {
    return this.getOrdersService().acceptOrRejectRequestQuoteVersion(quoteRequestId, status, reason);
  }
}
